import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AutoshipCustomerApiResponse } from '@libs/common-types';

import { useUserSession } from '../../hooks/useUserSession';
import { getCustomerUuid } from '../../utils/getCustomerUuid';
import { fetchAutoshipSubscriptions } from './fetchAutoshipSubscriptions';

const QUERY_KEY = 'autoship-customer-subscriptions';
const ONE_HOUR = 60 * 60 * 1000;

export type AutoshipSubscriptionsQueryOptions = Pick<
    UseQueryOptions<AutoshipCustomerApiResponse>,
    'refetchOnMount' | 'staleTime'
>;

export const autoshipSubscriptionsQueryFactory = (
    customerUuid?: string,
    options?: AutoshipSubscriptionsQueryOptions
) => {
    const { staleTime = ONE_HOUR, ...queryOptionsOverrides } = options ?? {};

    return queryOptions({
        ...queryOptionsOverrides,
        staleTime,
        queryKey: [QUERY_KEY, customerUuid],
        queryFn: customerUuid ? fetchAutoshipSubscriptions : skipToken,
    });
};

export const useAutoshipSubscriptionsQuery = (
    options?: AutoshipSubscriptionsQueryOptions
): UseQueryResult<AutoshipCustomerApiResponse> => {
    const { user } = useUserSession();
    const customerUuid = getCustomerUuid(user);

    return useQuery(autoshipSubscriptionsQueryFactory(customerUuid, options));
};

export const useInvalidateAutoshipSubscriptions = () => {
    const queryClient = useQueryClient();

    return () =>
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEY],
        });
};
